import React, { useState, useEffect, useCallback } from "react";
import {
  Wallet, Landmark, Smartphone, Scale, CheckCircle2, Lock, Plus, Trash2,
  Building2, Store, AlertTriangle, BookOpenCheck,
} from "lucide-react";
import { Card, Spinner, PageHeader, Badge, Button, Input } from "../lib/ui";
import { useToast } from "../lib/toast";
import api from "../lib/api";

const rp = (v) => `Rp ${Number(v || 0).toLocaleString("id-ID")}`;

const NAMED_BANKS = [
  { key: "bca", label: "Bank BCA" },
  { key: "bri", label: "Bank BRI" },
  { key: "mandiri", label: "Bank Mandiri" },
  { key: "cimb", label: "Bank CIMB Niaga" },
];
const NAMED_WALLETS = [
  { key: "ovo", label: "OVO Merchant" },
  { key: "gopay", label: "GoPay Merchant" },
  { key: "shopeepay", label: "ShopeePay Merchant" },
];

const emptyForm = () => ({
  cash_drawer: "", petty_cash: "",
  bank_bca: "", bank_bri: "", bank_mandiri: "", bank_cimb: "",
  other_banks: [],
  wallet_ovo: "", wallet_gopay: "", wallet_shopeepay: "",
  other_wallets: [],
  receivables: "", payables: "",
});

function toForm(data) {
  const f = emptyForm();
  if (!data) return f;
  f.cash_drawer = data.cash_drawer || "";
  f.petty_cash = data.petty_cash || "";
  f.receivables = data.receivables || "";
  f.payables = data.payables || "";
  (data.banks || []).forEach((b) => {
    if (b.key === "other") f.other_banks.push({ name: b.name, amount: b.amount });
    else f[`bank_${b.key}`] = b.amount;
  });
  (data.wallets || []).forEach((w) => {
    if (w.key === "other") f.other_wallets.push({ name: w.name, amount: w.amount });
    else f[`wallet_${w.key}`] = w.amount;
  });
  return f;
}

function toPayload(f) {
  const banks = NAMED_BANKS.filter((b) => Number(f[`bank_${b.key}`] || 0) > 0)
    .map((b) => ({ key: b.key, amount: Number(f[`bank_${b.key}`]) }));
  f.other_banks.forEach((r) => { if (Number(r.amount || 0) > 0) banks.push({ key: "other", name: r.name, amount: Number(r.amount) }); });
  const wallets = NAMED_WALLETS.filter((w) => Number(f[`wallet_${w.key}`] || 0) > 0)
    .map((w) => ({ key: w.key, amount: Number(f[`wallet_${w.key}`]) }));
  f.other_wallets.forEach((r) => { if (Number(r.amount || 0) > 0) wallets.push({ key: "other", name: r.name, amount: Number(r.amount) }); });
  return {
    cash_drawer: Number(f.cash_drawer || 0), petty_cash: Number(f.petty_cash || 0),
    banks, wallets,
    receivables: Number(f.receivables || 0), payables: Number(f.payables || 0),
  };
}

function formTotals(f) {
  const p = toPayload(f);
  const assets = p.cash_drawer + p.petty_cash + p.receivables
    + p.banks.reduce((a, b) => a + b.amount, 0)
    + p.wallets.reduce((a, b) => a + b.amount, 0);
  return { assets: Math.round(assets * 100) / 100, payables: p.payables, equity: Math.round((assets - p.payables) * 100) / 100 };
}

function OtherRows({ rows, setRows, placeholder, testPrefix, editable }) {
  const update = (i, k, v) => setRows(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="flex items-end gap-2" data-testid={`${testPrefix}-row-${i}`}>
          <div className="flex-1"><Input label={i === 0 ? "Nama" : undefined} value={r.name} placeholder={placeholder}
            onChange={(e) => update(i, "name", e.target.value)} disabled={!editable} data-testid={`${testPrefix}-name-${i}`} /></div>
          <div className="flex-1"><Input label={i === 0 ? "Saldo" : undefined} type="number" min="0" value={r.amount} placeholder="0"
            onChange={(e) => update(i, "amount", e.target.value)} disabled={!editable} data-testid={`${testPrefix}-amount-${i}`} /></div>
          {editable && (
            <button onClick={() => setRows(rows.filter((_, idx) => idx !== i))} className="mb-2 rounded-lg p-2 text-danger-text hover:bg-red-50" data-testid={`${testPrefix}-remove-${i}`}>
              <Trash2 size={15} />
            </button>
          )}
        </div>
      ))}
      {editable && (
        <Button variant="outline" size="sm" onClick={() => setRows([...rows, { name: "", amount: "" }])} data-testid={`${testPrefix}-add`}>
          <Plus size={13} /> Tambah
        </Button>
      )}
    </div>
  );
}

export default function BeginningBalance() {
  const toast = useToast();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entityId, setEntityId] = useState("central");
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (keepEntity) => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrity/beginning-balance");
      setState(data);
      const eid = keepEntity || "central";
      setEntityId(eid);
      const ent = data.entities.find((e) => e.id === eid);
      setForm(toForm(ent?.data));
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat Beginning Balance"); }
    finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  if (loading || !state) return <div className="flex h-64 items-center justify-center"><Spinner className="h-8 w-8" /></div>;

  const editable = state.editable;
  const entity = state.entities.find((e) => e.id === entityId);
  const totals = formTotals(form);
  const savedCount = state.entities.filter((e) => e.saved).length;

  const selectEntity = (id) => {
    setEntityId(id);
    const ent = state.entities.find((e) => e.id === id);
    setForm(toForm(ent?.data));
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/integrity/beginning-balance/${entityId}`, toPayload(form));
      toast.success(`Saldo awal ${entity?.name} tersimpan — jurnal diposting otomatis`);
      await load(entityId);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
    finally { setBusy(false); }
  };

  const markComplete = async () => {
    if (!window.confirm("Tandai Beginning Balance SELESAI? Status ini menjadi syarat verifikasi Go-Live.")) return;
    setBusy(true);
    try {
      await api.post("/integrity/beginning-balance/complete");
      toast.success("Beginning Balance ditandai selesai");
      await load(entityId);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  const num = (k) => (
    <Input type="number" min="0" value={form[k]} placeholder="0" disabled={!editable}
      onChange={(e) => setForm((s) => ({ ...s, [k]: e.target.value }))} data-testid={`bb-${k.replace(/_/g, "-")}`} />
  );

  return (
    <div data-testid="beginning-balance-page" className="animate-fade-up pb-24 lg:pb-2">
      <PageHeader title="Beginning Balance" subtitle="Saldo awal keuangan per lokasi — jurnal saldo awal dibuat otomatis oleh Accounting Engine" icon={Wallet} />

      {/* Status banner */}
      <div className={`mb-5 flex flex-col gap-3 rounded-2xl border-2 p-5 sm:flex-row sm:items-center sm:justify-between ${state.completed ? "border-emerald-300 bg-emerald-50" : editable ? "border-amber-300 bg-amber-50" : "border-stone-300 bg-stone-50"}`} data-testid="bb-status-banner">
        <div className="flex items-center gap-3">
          <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${state.completed ? "bg-emerald-500 text-white" : editable ? "bg-amber-400 text-amber-900" : "bg-stone-400 text-white"}`}>
            {state.completed ? <CheckCircle2 size={24} /> : editable ? <BookOpenCheck size={24} /> : <Lock size={24} />}
          </div>
          <div>
            <p className="font-heading text-lg font-bold text-ink" data-testid="bb-status-label">
              {state.completed ? "SELESAI — siap verifikasi Go-Live" : editable ? `Pengisian Saldo Awal (${savedCount}/${state.entities.length} lokasi tersimpan)` : "TERKUNCI — Production Mode (arsip historis)"}
            </p>
            <p className="text-xs text-ink-soft">
              {editable
                ? "Isi posisi keuangan nyata setiap lokasi pada hari pertama. Persediaan TIDAK diisi di sini — gunakan First Opening Stock Take di menu Stock Take."
                : "Beginning Balance hanya dapat diubah pada Implementation Mode."}
              {state.completed && state.completed_by ? ` · Ditandai selesai oleh ${state.completed_by}` : ""}
            </p>
          </div>
        </div>
        {editable && !state.completed && (
          <Button onClick={markComplete} loading={busy} variant="outline" className="shrink-0" data-testid="bb-mark-complete">
            <CheckCircle2 size={15} /> Tandai Selesai
          </Button>
        )}
      </div>

      {editable && !state.fresh_start_executed_at && (
        <div className="mb-5 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="bb-freshstart-warning">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>Fresh Start cleanup belum dijalankan. Perhatian: menjalankan pembersihan Fresh Start akan <b>me-reset</b> semua saldo awal — isi Beginning Balance <b>setelah</b> pembersihan.</span>
        </div>
      )}

      {/* Entity tabs */}
      <div className="mb-4 flex flex-wrap gap-2" data-testid="bb-entity-tabs">
        {state.entities.map((e) => (
          <button key={e.id} onClick={() => selectEntity(e.id)} data-testid={`bb-entity-${e.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${entityId === e.id ? "border-primary bg-primary text-white" : "border-line bg-white text-ink-soft hover:border-primary"}`}>
            {e.id === "central" ? <Building2 size={14} /> : <Store size={14} />} {e.name}
            {e.saved && <CheckCircle2 size={14} className={entityId === e.id ? "text-white" : "text-success-text"} />}
          </button>
        ))}
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          {/* Kas */}
          <Card data-testid="bb-section-cash">
            <h3 className="mb-3 flex items-center gap-2 font-heading font-bold text-ink"><Wallet size={16} className="text-primary" /> Kas — {entity?.name}</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div><p className="mb-1 text-xs font-semibold text-ink-soft">Cash Drawer (Kas Operasional)</p>{num("cash_drawer")}</div>
              <div><p className="mb-1 text-xs font-semibold text-ink-soft">Kas Kecil (Petty Cash)</p>{num("petty_cash")}</div>
            </div>
          </Card>

          {/* Bank */}
          <Card data-testid="bb-section-banks">
            <h3 className="mb-3 flex items-center gap-2 font-heading font-bold text-ink"><Landmark size={16} className="text-primary" /> Rekening Bank</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              {NAMED_BANKS.map((b) => (
                <div key={b.key}><p className="mb-1 text-xs font-semibold text-ink-soft">{b.label}</p>{num(`bank_${b.key}`)}</div>
              ))}
            </div>
            <p className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-ink-mute">Bank Lainnya</p>
            <OtherRows rows={form.other_banks} setRows={(r) => setForm((s) => ({ ...s, other_banks: r }))}
              placeholder="cth: Bank Jago" testPrefix="bb-other-bank" editable={editable} />
          </Card>

          {/* Wallet */}
          <Card data-testid="bb-section-wallets">
            <h3 className="mb-3 flex items-center gap-2 font-heading font-bold text-ink"><Smartphone size={16} className="text-primary" /> Merchant Wallet</h3>
            <div className="grid gap-3 sm:grid-cols-3">
              {NAMED_WALLETS.map((w) => (
                <div key={w.key}><p className="mb-1 text-xs font-semibold text-ink-soft">{w.label}</p>{num(`wallet_${w.key}`)}</div>
              ))}
            </div>
            <p className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-ink-mute">Wallet Lainnya</p>
            <OtherRows rows={form.other_wallets} setRows={(r) => setForm((s) => ({ ...s, other_wallets: r }))}
              placeholder="cth: DANA Merchant" testPrefix="bb-other-wallet" editable={editable} />
          </Card>

          {/* Outstanding */}
          <Card data-testid="bb-section-outstanding">
            <h3 className="mb-3 flex items-center gap-2 font-heading font-bold text-ink"><Scale size={16} className="text-primary" /> Saldo Berjalan</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div><p className="mb-1 text-xs font-semibold text-ink-soft">Piutang Usaha (Accounts Receivable)</p>{num("receivables")}</div>
              <div><p className="mb-1 text-xs font-semibold text-ink-soft">Hutang Supplier (Accounts Payable)</p>{num("payables")}</div>
            </div>
          </Card>
        </div>

        {/* Summary + save */}
        <div className="space-y-4">
          <Card className="border-2 border-primary/20" data-testid="bb-summary">
            <h3 className="mb-3 font-heading font-bold text-ink">Ringkasan — {entity?.name}</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-ink-soft">Total Aset</span><b data-testid="bb-total-assets">{rp(totals.assets)}</b></div>
              <div className="flex justify-between"><span className="text-ink-soft">Hutang Supplier</span><b>{rp(totals.payables)}</b></div>
              <div className="flex justify-between border-t border-line pt-2"><span className="font-semibold text-ink">Modal Disetor (otomatis)</span>
                <b className={totals.equity < 0 ? "text-danger-text" : "text-success-text"} data-testid="bb-equity">{rp(totals.equity)}</b></div>
            </div>
            <p className="mt-3 text-[11px] text-ink-mute">Satu jurnal saldo awal yang seimbang diposting otomatis per lokasi (Dr Kas/Bank/Wallet/Piutang · Cr Hutang · penyeimbang Modal Disetor). Tanpa jurnal manual.</p>
            {editable && (
              <Button onClick={save} loading={busy} className="mt-4 w-full" data-testid="bb-save">
                Simpan Saldo Awal {entity?.name}
              </Button>
            )}
            {entity?.saved && (
              <div className="mt-3 flex items-center gap-1.5">
                <Badge tone="success" data-testid="bb-saved-badge"><CheckCircle2 size={12} /> Tersimpan & jurnal diposting</Badge>
              </div>
            )}
          </Card>

          <Card data-testid="bb-entities-overview">
            <h3 className="mb-2 font-heading text-sm font-bold text-ink">Status per Lokasi</h3>
            <div className="space-y-1.5">
              {state.entities.map((e) => (
                <div key={e.id} className="flex items-center justify-between border-b border-line py-1.5 text-sm last:border-0">
                  <span className="text-ink-soft">{e.name}</span>
                  {e.saved
                    ? <span className="flex items-center gap-2"><b className="text-xs">{rp(e.total_assets)}</b><Badge tone="success">Tersimpan</Badge></span>
                    : <Badge tone="mute">Belum diisi</Badge>}
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
